// Translations for BracketFlow App
// Supported languages: English, Arabic, French, Spanish

export const translations = {
  en: {
    // Common
    appName: "Botoolat",
    tagline: "Create. Compete. Crown a Champion.",
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    confirm: "Confirm",
    delete: "Delete",
    edit: "Edit",
    view: "View",
    share: "Share",
    export: "Export",
    back: "Back",
    next: "Next",

    // Auth
    auth: {
      signIn: "Sign In",
      signUp: "Sign Up",
      signOut: "Sign Out",
      login: "Log In",
      register: "Create Account",
      email: "Email",
      password: "Password",
      name: "Name",
      continueWithGoogle: "Continue with Google",
      or: "or",
      noAccount: "Don't have an account?",
      haveAccount: "Already have an account?",
      loginTitle: "Welcome back",
      loginSubtitle: "Sign in to create and manage your tournaments",
      registerTitle: "Create your account",
      registerSubtitle: "Sign up to start building tournaments",
      unverified: "Email not verified",
      signInRequired: "Please sign in to create tournaments",
      loggingIn: "Signing in...",
      emailPlaceholder: "you@example.com",
      passwordPlaceholder: "Your password",
      namePlaceholder: "Your name",
      account: "Account",
      profile: "Profile",
      emailVerification: "Email Verification",
      verified: "Verified",
      notVerified: "Not Verified",
      verifyEmailTitle: "Verify your email",
      verifyEmailDesc: "We sent a 6-digit code to your email. Enter it below to verify. Verification is optional — you can keep using every feature either way.",
      alreadyVerified: "Your email address is verified.",
      googleVerifiedNote: "Signed in with Google — your email is already verified.",
      sendCode: "Send verification email",
      resendCode: "Resend email",
      enterCode: "Enter 6-digit code",
      verifyBtn: "Verify Email",
      verifying: "Verifying...",
      sending: "Sending...",
      codeSent: "Verification email sent — check your inbox.",
      verifiedToast: "Email verified!",
      passwordSection: "Password Sign-In",
      createPasswordDesc: "You signed in with Google. Create a password to also sign in with your email and password.",
      changePasswordDesc: "Update the password you use to sign in with email.",
      newPassword: "New password",
      createPassword: "Create Password",
      updatePassword: "Update Password",
      savingPassword: "Saving...",
      passwordSetToast: "Password saved — you can now sign in with your email and password.",
      owner: "Owner",
      ownerAccess: "Super Admin Access",
      enterAdminPassword: "Enter your Super Admin password to open the dashboard.",
      adminPasswordPlaceholder: "Super Admin password",
      verifyOwner: "Open Super Admin",
      verifyingOwner: "Verifying...",
    },
    admin: {
      title: "Super Admin",
      subtitle: "Platform analytics & management",
      backToApp: "Back to app",
      exitAdmin: "Exit Admin",
      tabOverview: "Overview",
      tabAnalytics: "Analytics",
      tabUsers: "Users",
      tabTournaments: "Tournaments",
      tabLogs: "Activity",
      totalVisitors: "Total Visitors",
      uniqueVisitors: "Unique Visitors",
      registeredUsers: "Registered Users",
      totalLogins: "Total Logins",
      activeUsers: "Active Users (7d)",
      totalTournaments: "Total Tournaments",
      tournamentsToday: "Created Today",
      tournamentsMonth: "Created This Month",
      suspendedUsers: "Suspended Users",
      last30days: "Last 30 days",
      searchUsers: "Search users by name or email",
      searchTournaments: "Search tournaments by name or code",
      user: "User",
      status: "Status",
      provider: "Provider",
      joined: "Joined",
      tournaments: "Tournaments",
      actions: "Actions",
      suspend: "Suspend",
      unsuspend: "Unsuspend",
      active: "Active",
      suspended: "Suspended",
      verified: "Verified",
      unverified: "Unverified",
      name: "Name",
      code: "Code",
      sport: "Sport",
      format: "Format",
      created: "Created",
      players: "Players",
      delete: "Delete",
      deleteConfirm: "Delete this tournament permanently?",
      noData: "No data yet",
      when: "When",
      action: "Action",
      details: "Details",
      loading: "Loading...",
      suspendedToast: "User suspended",
      unsuspendedToast: "User unsuspended",
      deletedToast: "Tournament deleted",
      verifyFailed: "Incorrect Super Admin password.",
      needVerify: "Please verify your Super Admin password first.",
      visitors: "Visitors",
      logins: "Logins",
      newUsers: "New Users",
      metric: "Metric",
    },

    
    // Home Page
    home: {
      title: "Where Every Competition Begins",
      subtitle: "Create professional tournaments in minutes. Choose a format, add participants, track results, and automatically generate standings and brackets.",
      createNew: "Create Tournament",
      getStarted: "Get Started",
      createTournamentDesc: "Choose a format, add teams or players, set the rules, and start your competition.",
      viewTournament: "View Tournament",
      viewTournamentDesc: "Follow live standings, match results, schedules, and bracket progression.",
      tournamentName: "Tournament Name",
      enterCode: "Tournament Code",
      viewBtn: "View",
      yourTournaments: "Your Tournaments",
      noTournaments: "No tournaments yet. Create your first one!",
      resume: "Resume",
      completed: "Complete",
      players: "players",
      teams: "teams",
      deleteTournament: "Delete Tournament?",
      deleteConfirm: "This will permanently delete this tournament. This action cannot be undone.",
    },
    
    // Setup Page
    setup: {
      step1: "Step 1: Basic Info",
      step2: "Step 2: Format",
      step3Title: "Step 3: Participants",
      step3TitleLeague: "Step 3: Teams",
      tournamentName: "Tournament Name",
      tournamentCode: "Tournament Code",
      codeHint: "Share this code and tournament name with others to let them view the tournament",
      codeMinLength: "Tournament code must be at least 4 characters",
      enablePlayerStats: "Enable Player Statistics",
      enablePlayerStatsDesc: "Track goals and assists per player and see tournament leaders",
      playerName: "Player name",
      addPlayer: "Add player",
      selectSport: "Select Sport",
      customSport: "Custom Sport Name",
      chooseFormat: "Next: Choose Format",
      league: "League",
      leagueDesc: "Round-robin format where everyone plays each other",
      bracket: "Bracket",
      bracketDesc: "Elimination tournament with knockout rounds",
      optionalFeatures: "Optional Features (Choose One)",
      doubleElimination: "Double Elimination",
      doubleElimDesc: "Lose twice to be eliminated. Losers get a second chance.",
      bronzeMedal: "Bronze Medal Match",
      bronzeMedalDesc: "3rd place playoff. Semifinal losers compete for bronze.",
      enterParticipant: "Enter participant name",
      enterTeam: "Enter team name",
      clickToAddLogo: "Click on the number to add a logo",
      addAtLeast: "Add at least 2",
      participants: "participants",
      shuffle: "Shuffle",
      generateBracket: "Generate Bracket",
      generateLeague: "Generate League",
      editTournament: "Edit Tournament",
      saveChanges: "Save Changes",
      rebuildTitle: "Rebuild Tournament?",
      rebuildWarning: "Changing the participants or format will rebuild the tournament. This will reset all match results and cannot be undone.",
      rebuildConfirm: "Rebuild & Reset",
      nextAddParticipants: "Next: Add Participants",
      nameExists: "This tournament name already exists",
      codeExists: "This tournament code already exists",
      bothExist: "Both name and code already exist",
    },
    
    // Bracket Page
    bracket: {
      winnersBracket: "Winners Bracket",
      losersBracket: "Losers Bracket",
      grandFinal: "Grand Final",
      grandFinalReset: "Grand Final Reset",
      bronzeMatch: "Bronze Medal Match",
      thirdPlace: "3rd Place Playoff",
      champion: "Champion",
      finalStandings: "Final Standings",
      gamesLeft: "games left",
      gameLeft: "game left",
      viewOnly: "View Only",
      waitingForSemis: "Waiting for Semis",
      winnersChampVsLosers: "Winners Champion vs Losers Champion",
      rank: "Rank",
      player: "Player",
      wins: "Wins",
      losses: "Losses",
      points: "Points",
      enterScores: "Enter Scores",
      vs: "VS",
      bye: "BYE",
      tbd: "TBD",
    },
    
    // League Page
    league: {
      standings: "Standings",
      matches: "Matches",
      pendingMatches: "Pending Matches",
      completedMatches: "Completed Matches",
      round: "Round",
      played: "Played",
      won: "Won",
      drawn: "Drawn",
      lost: "Lost",
      goalsFor: "Goals For",
      goalsAgainst: "Goals Against",
      goalDiff: "Goal Diff",
      viewChampion: "View Champion",
    },
    
    // Champion Page
    champion: {
      tournamentComplete: "Tournament Complete",
      newTournament: "New Tournament",
      viewBracket: "View Bracket",
      viewLeague: "View League",
      roster: "Team Roster",
    },

    // Player Statistics
    stats: {
      playerStats: "Player Statistics",
      topScorer: "Top Scorer",
      topAssists: "Top Assists",
      topScorers: "Top Scorers",
      topAssisters: "Top Assisters",
      topGA: "Top G+A",
      topGADesc: "Goals + Assists combined",
      total: "Total",
      allPlayers: "All Players",
      player: "Player",
      team: "Team",
      goals: "Goals",
      assists: "Assists",
      goalsShort: "goals",
      goalsAbbr: "G",
      assistsAbbr: "A",
      enterPlayerStats: "Enter goals and assists per player",
      goalsMustEqualScore: "Total goals must equal the team's score",
      noStatsYet: "No player statistics recorded yet",
    },
    
    // Share Dialog
    shareDialog: {
      title: "Share Tournament",
      description: "Share this link with others to let them view the tournament",
      shareLink: "Share Link",
      orShare: "Or share using name and code:",
      name: "Name",
      code: "Code",
      copied: "Link copied to clipboard!",
    },
    
    // Toasts
    toast: {
      tournamentCreated: "Tournament created!",
      tournamentUpdated: "Tournament updated!",
      scoreRecorded: "Score recorded!",
      matchReset: "Match reset!",
      logoAdded: "Logo added!",
      logoRemoved: "Logo removed",
      logoFailed: "Failed to upload logo",
      tournamentDeleted: "Tournament deleted",
      tournamentFound: "Tournament found!",
      tournamentNotFound: "Tournament not found",
      enterValidScores: "Please enter valid scores",
      scoresCannotBeTied: "Scores cannot be tied - there must be a winner",
      scoresCannotBeNegative: "Scores cannot be negative",
      noPermission: "You don't have permission to edit scores",
      noSchedulePermission: "You don't have permission to edit schedule",
      logoTooLarge: "Logo must be less than 500KB",
      addAtLeast2: "Add at least 2 participants",
      bronzeNeeds4: "Bronze Medal Match requires at least 4 participants",
      viewOnly: "View only - you cannot edit scores",
      viewOnlySchedule: "View only - you cannot edit schedule",
      viewOnlyReset: "View only - you cannot reset matches",
      linkCopied: "Link copied to clipboard!",
      codeCopied: "Tournament code copied!",
      linkCopyFailed: "Failed to copy link",
      enterNameAndCode: "Please enter tournament name and code",
      enterCode: "Please enter the tournament code",
      shuffled: "Participants shuffled!",
      scheduleUpdated: "Schedule updated!",
      createFailed: "Failed to create tournament",
      nameExists: "Tournament name already exists. Please use a different name.",
      codeExists: "Tournament code already exists. Please use a different code.",
      bothExist: "Tournament name and code already exist. Please use different values.",
    },
    
    // Sports
    sports: {
      football: "Football",
      basketball: "Basketball",
      badminton: "Badminton",
      pingpong: "Ping Pong",
      tennis: "Tennis",
      volleyball: "Volleyball",
      esports: "Esports",
      boardgames: "Board Games",
      custom: "Custom Sport",
    },
  },
  
  ar: {
    auth: {
      signIn: "تسجيل الدخول",
      signUp: "إنشاء حساب",
      signOut: "تسجيل الخروج",
      login: "دخول",
      register: "إنشاء حساب",
      email: "البريد الإلكتروني",
      password: "كلمة المرور",
      name: "الاسم",
      continueWithGoogle: "المتابعة عبر Google",
      or: "أو",
      noAccount: "ليس لديك حساب؟",
      haveAccount: "لديك حساب بالفعل؟",
      loginTitle: "مرحبًا بعودتك",
      loginSubtitle: "سجّل الدخول لإنشاء بطولاتك وإدارتها",
      registerTitle: "أنشئ حسابك",
      registerSubtitle: "سجّل لبدء إنشاء البطولات",
      unverified: "البريد غير مُوثَّق",
      signInRequired: "يرجى تسجيل الدخول لإنشاء البطولات",
      loggingIn: "جارٍ تسجيل الدخول...",
      emailPlaceholder: "you@example.com",
      passwordPlaceholder: "كلمة المرور",
      namePlaceholder: "اسمك",
      account: "الحساب",
      profile: "الملف الشخصي",
      emailVerification: "توثيق البريد الإلكتروني",
      verified: "مُوثَّق",
      notVerified: "غير مُوثَّق",
      verifyEmailTitle: "وثّق بريدك الإلكتروني",
      verifyEmailDesc: "أرسلنا رمزًا من 6 أرقام إلى بريدك. أدخله أدناه للتوثيق. التوثيق اختياري — يمكنك استخدام جميع الميزات في كل الأحوال.",
      alreadyVerified: "تم توثيق عنوان بريدك الإلكتروني.",
      googleVerifiedNote: "سجّلت الدخول عبر Google — بريدك مُوثَّق بالفعل.",
      sendCode: "إرسال بريد التوثيق",
      resendCode: "إعادة الإرسال",
      enterCode: "أدخل الرمز المكوّن من 6 أرقام",
      verifyBtn: "توثيق البريد",
      verifying: "جارٍ التوثيق...",
      sending: "جارٍ الإرسال...",
      codeSent: "تم إرسال بريد التوثيق — تحقق من صندوق الوارد.",
      verifiedToast: "تم توثيق البريد!",
      passwordSection: "الدخول بكلمة المرور",
      createPasswordDesc: "سجّلت الدخول عبر Google. أنشئ كلمة مرور لتتمكن أيضًا من الدخول ببريدك وكلمة المرور.",
      changePasswordDesc: "حدّث كلمة المرور التي تستخدمها للدخول بالبريد.",
      newPassword: "كلمة مرور جديدة",
      createPassword: "إنشاء كلمة مرور",
      updatePassword: "تحديث كلمة المرور",
      savingPassword: "جارٍ الحفظ...",
      passwordSetToast: "تم حفظ كلمة المرور — يمكنك الآن الدخول ببريدك وكلمة المرور.",
    },
    stats: {
      playerStats: "إحصائيات اللاعبين",
      topScorer: "أفضل هدّاف",
      topAssists: "أكثر التمريرات الحاسمة",
      topScorers: "أفضل الهدّافين",
      topAssisters: "أصحاب التمريرات الحاسمة",
      topGA: "الأهداف + التمريرات",
      topGADesc: "مجموع الأهداف والتمريرات الحاسمة",
      total: "الإجمالي",
      allPlayers: "جميع اللاعبين",
      player: "اللاعب",
      team: "الفريق",
      goals: "الأهداف",
      assists: "التمريرات الحاسمة",
      goalsShort: "أهداف",
      goalsAbbr: "هـ",
      assistsAbbr: "ت",
      enterPlayerStats: "أدخل الأهداف والتمريرات لكل لاعب",
      goalsMustEqualScore: "يجب أن يساوي مجموع الأهداف نتيجة الفريق",
      noStatsYet: "لا توجد إحصائيات لاعبين بعد",
    },
    // Common
    appName: "Botoolat",
    tagline: "أنشئ. تنافس. توّج البطل.",
    loading: "جاري التحميل...",
    save: "حفظ",
    cancel: "إلغاء",
    close: "إغلاق",
    confirm: "تأكيد",
    delete: "حذف",
    edit: "تعديل",
    view: "عرض",
    share: "مشاركة",
    export: "تصدير",
    back: "رجوع",
    next: "التالي",
    
    // Home Page
    home: {
      title: "حيث تبدأ كل منافسة",
      subtitle: "أنشئ بطولات احترافية في دقائق. اختر صيغة، وأضف المشاركين، وتابع النتائج، وأنشئ الترتيب والأقواس تلقائيًا.",
      createNew: "إنشاء بطولة",
      getStarted: "ابدأ الآن",
      createTournamentDesc: "اختر صيغة، أضف الفرق أو اللاعبين، حدّد القواعد، وابدأ منافستك.",
      viewTournament: "عرض البطولة",
      viewTournamentDesc: "تابع الترتيب المباشر ونتائج المباريات والجداول وتقدم الأقواس.",
      tournamentName: "اسم البطولة",
      enterCode: "رمز البطولة",
      viewBtn: "عرض",
      yourTournaments: "بطولاتك",
      noTournaments: "لا توجد بطولات بعد. أنشئ أول بطولة!",
      resume: "استئناف",
      completed: "مكتملة",
      players: "لاعبين",
      teams: "فرق",
      deleteTournament: "حذف البطولة؟",
      deleteConfirm: "سيتم حذف هذه البطولة نهائياً. لا يمكن التراجع عن هذا الإجراء.",
    },
    
    // Setup Page
    setup: {
      step1: "الخطوة 1: المعلومات الأساسية",
      step2: "الخطوة 2: الصيغة",
      step3Title: "الخطوة 3: المشاركون",
      step3TitleLeague: "الخطوة 3: الفرق",
      tournamentName: "اسم البطولة",
      tournamentCode: "رمز البطولة",
      codeHint: "شارك هذا الرمز واسم البطولة مع الآخرين للسماح لهم بمشاهدة البطولة",
      enablePlayerStats: "تفعيل إحصائيات اللاعبين",
      enablePlayerStatsDesc: "تتبّع الأهداف والتمريرات لكل لاعب وشاهد المتصدرين",
      playerName: "اسم اللاعب",
      addPlayer: "إضافة لاعب",
      codeMinLength: "يجب أن يتكوّن رمز البطولة من 4 أحرف على الأقل",
      selectSport: "اختر الرياضة",
      customSport: "اسم الرياضة المخصصة",
      chooseFormat: "التالي: اختر الصيغة",
      league: "دوري",
      leagueDesc: "صيغة الدوري حيث يلعب الجميع ضد بعضهم",
      bracket: "بطولة خروج المغلوب",
      bracketDesc: "بطولة إقصائية بجولات خروج المغلوب",
      optionalFeatures: "ميزات اختيارية (اختر واحدة)",
      doubleElimination: "إقصاء مزدوج",
      doubleElimDesc: "تخسر مرتين للإقصاء. الخاسرون يحصلون على فرصة ثانية.",
      bronzeMedal: "مباراة الميدالية البرونزية",
      bronzeMedalDesc: "تحديد المركز الثالث. خاسرو نصف النهائي يتنافسون على البرونزية.",
      enterParticipant: "أدخل اسم المشارك",
      enterTeam: "أدخل اسم الفريق",
      clickToAddLogo: "اضغط على الرقم لإضافة شعار",
      addAtLeast: "أضف على الأقل 2",
      participants: "مشاركين",
      shuffle: "خلط",
      generateBracket: "إنشاء الجدول",
      generateLeague: "إنشاء الدوري",
      editTournament: "تعديل البطولة",
      saveChanges: "حفظ التغييرات",
      rebuildTitle: "إعادة بناء البطولة؟",
      rebuildWarning: "تغيير المشاركين أو الصيغة سيعيد بناء البطولة. سيؤدي ذلك إلى إعادة تعيين جميع نتائج المباريات ولا يمكن التراجع عنه.",
      rebuildConfirm: "إعادة البناء والتعيين",
      nextAddParticipants: "التالي: إضافة المشاركين",
      nameExists: "اسم البطولة موجود مسبقاً",
      codeExists: "رمز البطولة موجود مسبقاً",
      bothExist: "الاسم والرمز موجودان مسبقاً",
    },
    
    // Bracket Page
    bracket: {
      winnersBracket: "جدول الفائزين",
      losersBracket: "جدول الخاسرين",
      grandFinal: "النهائي الكبير",
      grandFinalReset: "إعادة النهائي الكبير",
      bronzeMatch: "مباراة الميدالية البرونزية",
      thirdPlace: "تحديد المركز الثالث",
      champion: "البطل",
      finalStandings: "الترتيب النهائي",
      gamesLeft: "مباريات متبقية",
      gameLeft: "مباراة متبقية",
      viewOnly: "عرض فقط",
      waitingForSemis: "في انتظار نصف النهائي",
      winnersChampVsLosers: "بطل الفائزين ضد بطل الخاسرين",
      rank: "الترتيب",
      player: "اللاعب",
      wins: "انتصارات",
      losses: "خسائر",
      points: "نقاط",
      enterScores: "أدخل النتائج",
      vs: "ضد",
      bye: "إعفاء",
      tbd: "يحدد لاحقاً",
    },
    
    // League Page
    league: {
      standings: "الترتيب",
      matches: "المباريات",
      pendingMatches: "المباريات المعلقة",
      completedMatches: "المباريات المكتملة",
      round: "الجولة",
      played: "لعب",
      won: "فاز",
      drawn: "تعادل",
      lost: "خسر",
      goalsFor: "أهداف له",
      goalsAgainst: "أهداف عليه",
      goalDiff: "فارق الأهداف",
      viewChampion: "عرض البطل",
    },
    
    // Champion Page
    champion: {
      tournamentComplete: "اكتملت البطولة",
      newTournament: "بطولة جديدة",
      roster: "تشكيلة الفريق",
      viewBracket: "عرض الجدول",
      viewLeague: "عرض الدوري",
    },
    
    // Share Dialog
    shareDialog: {
      title: "مشاركة البطولة",
      description: "شارك هذا الرابط مع الآخرين للسماح لهم بمشاهدة البطولة",
      shareLink: "رابط المشاركة",
      orShare: "أو شارك باستخدام الاسم والرمز:",
      name: "الاسم",
      code: "الرمز",
      copied: "تم نسخ الرابط!",
    },
    
    // Toasts
    toast: {
      tournamentCreated: "تم إنشاء البطولة!",
      tournamentUpdated: "تم تحديث البطولة!",
      scoreRecorded: "تم تسجيل النتيجة!",
      matchReset: "تمت إعادة تعيين المباراة!",
      logoAdded: "تمت إضافة الشعار!",
      logoRemoved: "تم حذف الشعار",
      logoFailed: "فشل في تحميل الشعار",
      tournamentDeleted: "تم حذف البطولة",
      tournamentFound: "تم العثور على البطولة!",
      tournamentNotFound: "البطولة غير موجودة",
      enterValidScores: "الرجاء إدخال نتائج صحيحة",
      scoresCannotBeTied: "لا يمكن تعادل النتائج - يجب أن يكون هناك فائز",
      scoresCannotBeNegative: "لا يمكن أن تكون النتائج سالبة",
      noPermission: "ليس لديك صلاحية لتعديل النتائج",
      noSchedulePermission: "ليس لديك صلاحية لتعديل الجدول",
      logoTooLarge: "يجب أن يكون الشعار أقل من 500 كيلوبايت",
      addAtLeast2: "أضف مشاركين على الأقل",
      bronzeNeeds4: "مباراة البرونزية تتطلب 4 مشاركين على الأقل",
      viewOnly: "عرض فقط - لا يمكنك تعديل النتائج",
      viewOnlySchedule: "عرض فقط - لا يمكنك تعديل الجدول",
      viewOnlyReset: "عرض فقط - لا يمكنك إعادة تعيين المباريات",
      linkCopied: "تم نسخ الرابط!",
      codeCopied: "تم نسخ رمز البطولة!",
      linkCopyFailed: "فشل في نسخ الرابط",
      enterNameAndCode: "الرجاء إدخال اسم البطولة والرمز",
      shuffled: "تم خلط المشاركين!",
      scheduleUpdated: "تم تحديث الجدول!",
      createFailed: "فشل في إنشاء البطولة",
      nameExists: "اسم البطولة موجود مسبقاً. الرجاء استخدام اسم مختلف.",
      codeExists: "رمز البطولة موجود مسبقاً. الرجاء استخدام رمز مختلف.",
      bothExist: "اسم ورمز البطولة موجودان مسبقاً. الرجاء استخدام قيم مختلفة.",
    },
    
    // Sports
    sports: {
      football: "كرة القدم",
      basketball: "كرة السلة",
      badminton: "الريشة الطائرة",
      pingpong: "تنس الطاولة",
      tennis: "التنس",
      volleyball: "الكرة الطائرة",
      esports: "الرياضات الإلكترونية",
      boardgames: "ألعاب الطاولة",
      custom: "رياضة مخصصة",
    },
  },
  
  fr: {
    auth: {
      signIn: "Se connecter",
      signUp: "S'inscrire",
      signOut: "Se déconnecter",
      login: "Connexion",
      register: "Créer un compte",
      email: "E-mail",
      password: "Mot de passe",
      name: "Nom",
      continueWithGoogle: "Continuer avec Google",
      or: "ou",
      noAccount: "Vous n'avez pas de compte ?",
      haveAccount: "Vous avez déjà un compte ?",
      loginTitle: "Bon retour",
      loginSubtitle: "Connectez-vous pour créer et gérer vos tournois",
      registerTitle: "Créez votre compte",
      registerSubtitle: "Inscrivez-vous pour commencer à créer des tournois",
      unverified: "E-mail non vérifié",
      signInRequired: "Veuillez vous connecter pour créer des tournois",
      loggingIn: "Connexion...",
      emailPlaceholder: "vous@exemple.com",
      passwordPlaceholder: "Votre mot de passe",
      namePlaceholder: "Votre nom",
      account: "Compte",
      profile: "Profil",
      emailVerification: "Vérification de l'e-mail",
      verified: "Vérifié",
      notVerified: "Non vérifié",
      verifyEmailTitle: "Vérifiez votre e-mail",
      verifyEmailDesc: "Nous avons envoyé un code à 6 chiffres à votre e-mail. Saisissez-le ci-dessous pour vérifier. La vérification est facultative — vous pouvez utiliser toutes les fonctionnalités dans tous les cas.",
      alreadyVerified: "Votre adresse e-mail est vérifiée.",
      googleVerifiedNote: "Connecté avec Google — votre e-mail est déjà vérifié.",
      sendCode: "Envoyer l'e-mail de vérification",
      resendCode: "Renvoyer l'e-mail",
      enterCode: "Saisissez le code à 6 chiffres",
      verifyBtn: "Vérifier l'e-mail",
      verifying: "Vérification...",
      sending: "Envoi...",
      codeSent: "E-mail de vérification envoyé — vérifiez votre boîte de réception.",
      verifiedToast: "E-mail vérifié !",
      passwordSection: "Connexion par mot de passe",
      createPasswordDesc: "Vous vous êtes connecté avec Google. Créez un mot de passe pour vous connecter aussi avec votre e-mail et mot de passe.",
      changePasswordDesc: "Mettez à jour le mot de passe que vous utilisez pour vous connecter par e-mail.",
      newPassword: "Nouveau mot de passe",
      createPassword: "Créer un mot de passe",
      updatePassword: "Mettre à jour le mot de passe",
      savingPassword: "Enregistrement...",
      passwordSetToast: "Mot de passe enregistré — vous pouvez maintenant vous connecter avec votre e-mail et mot de passe.",
    },
    stats: {
      playerStats: "Statistiques des joueurs",
      topScorer: "Meilleur buteur",
      topAssists: "Meilleures passes décisives",
      topScorers: "Meilleurs buteurs",
      topAssisters: "Meilleurs passeurs",
      topGA: "Top B+P",
      topGADesc: "Buts + passes décisives cumulés",
      total: "Total",
      allPlayers: "Tous les joueurs",
      player: "Joueur",
      team: "Équipe",
      goals: "Buts",
      assists: "Passes décisives",
      goalsShort: "buts",
      goalsAbbr: "B",
      assistsAbbr: "P",
      enterPlayerStats: "Saisissez les buts et passes décisives par joueur",
      goalsMustEqualScore: "Le total des buts doit être égal au score de l'équipe",
      noStatsYet: "Aucune statistique de joueur enregistrée",
    },
    // Common
    appName: "Botoolat",
    tagline: "Créez. Compétez. Couronnez un Champion.",
    loading: "Chargement...",
    save: "Enregistrer",
    cancel: "Annuler",
    close: "Fermer",
    confirm: "Confirmer",
    delete: "Supprimer",
    edit: "Modifier",
    view: "Voir",
    share: "Partager",
    export: "Exporter",
    back: "Retour",
    next: "Suivant",
    
    // Home Page
    home: {
      title: "Là où Commence Chaque Compétition",
      subtitle: "Créez des tournois professionnels en quelques minutes. Choisissez un format, ajoutez des participants, suivez les résultats et générez automatiquement les classements et les tableaux.",
      createNew: "Créer un Tournoi",
      getStarted: "Commencer",
      createTournamentDesc: "Choisissez un format, ajoutez des équipes ou des joueurs, définissez les règles et lancez votre compétition.",
      viewTournament: "Voir le Tournoi",
      viewTournamentDesc: "Suivez les classements en direct, les résultats des matchs, les calendriers et la progression du tableau.",
      tournamentName: "Nom du Tournoi",
      enterCode: "Code du Tournoi",
      viewBtn: "Voir",
      yourTournaments: "Vos Tournois",
      noTournaments: "Pas encore de tournois. Créez votre premier!",
      resume: "Reprendre",
      completed: "Terminé",
      players: "joueurs",
      teams: "équipes",
      deleteTournament: "Supprimer le Tournoi?",
      deleteConfirm: "Cela supprimera définitivement ce tournoi. Cette action est irréversible.",
    },
    
    // Setup Page
    setup: {
      step1: "Étape 1: Informations de Base",
      step2: "Étape 2: Format",
      step3Title: "Étape 3: Participants",
      step3TitleLeague: "Étape 3: Équipes",
      tournamentName: "Nom du Tournoi",
      tournamentCode: "Code du Tournoi",
      codeHint: "Partagez ce code et le nom du tournoi avec d'autres pour leur permettre de voir le tournoi",
      enablePlayerStats: "Activer les statistiques des joueurs",
      enablePlayerStatsDesc: "Suivez les buts et passes par joueur et voyez les leaders",
      playerName: "Nom du joueur",
      addPlayer: "Ajouter un joueur",
      codeMinLength: "Le code du tournoi doit comporter au moins 4 caractères",
      selectSport: "Sélectionner le Sport",
      customSport: "Nom du Sport Personnalisé",
      chooseFormat: "Suivant: Choisir le Format",
      league: "Ligue",
      leagueDesc: "Format championnat où tout le monde joue contre tout le monde",
      bracket: "Tableau",
      bracketDesc: "Tournoi à élimination directe",
      optionalFeatures: "Options (Choisir Une)",
      doubleElimination: "Double Élimination",
      doubleElimDesc: "Perdez deux fois pour être éliminé. Les perdants ont une seconde chance.",
      bronzeMedal: "Match pour la Médaille de Bronze",
      bronzeMedalDesc: "Match pour la 3ème place. Les perdants des demi-finales s'affrontent.",
      enterParticipant: "Entrez le nom du participant",
      enterTeam: "Entrez le nom de l'équipe",
      clickToAddLogo: "Cliquez sur le numéro pour ajouter un logo",
      addAtLeast: "Ajoutez au moins 2",
      participants: "participants",
      shuffle: "Mélanger",
      generateBracket: "Générer le Tableau",
      generateLeague: "Générer la Ligue",
      editTournament: "Modifier le Tournoi",
      saveChanges: "Enregistrer",
      rebuildTitle: "Reconstruire le Tournoi?",
      rebuildWarning: "Modifier les participants ou le format reconstruira le tournoi. Cela réinitialisera tous les résultats des matchs et est irréversible.",
      rebuildConfirm: "Reconstruire et Réinitialiser",
      nextAddParticipants: "Suivant: Ajouter des Participants",
      nameExists: "Ce nom de tournoi existe déjà",
      codeExists: "Ce code de tournoi existe déjà",
      bothExist: "Le nom et le code existent déjà",
    },
    
    // Bracket Page
    bracket: {
      winnersBracket: "Tableau des Gagnants",
      losersBracket: "Tableau des Perdants",
      grandFinal: "Grande Finale",
      grandFinalReset: "Grande Finale Reset",
      bronzeMatch: "Match pour le Bronze",
      thirdPlace: "Match pour la 3ème Place",
      champion: "Champion",
      finalStandings: "Classement Final",
      gamesLeft: "matchs restants",
      gameLeft: "match restant",
      viewOnly: "Lecture Seule",
      waitingForSemis: "En attente des Demi-finales",
      winnersChampVsLosers: "Champion des Gagnants vs Champion des Perdants",
      rank: "Rang",
      player: "Joueur",
      wins: "Victoires",
      losses: "Défaites",
      points: "Points",
      enterScores: "Entrer les Scores",
      vs: "VS",
      bye: "Exempt",
      tbd: "À Déterminer",
    },
    
    // League Page
    league: {
      standings: "Classement",
      matches: "Matchs",
      pendingMatches: "Matchs en Attente",
      completedMatches: "Matchs Terminés",
      round: "Journée",
      played: "Joués",
      won: "Gagnés",
      drawn: "Nuls",
      lost: "Perdus",
      goalsFor: "Buts Pour",
      goalsAgainst: "Buts Contre",
      goalDiff: "Diff. Buts",
      viewChampion: "Voir le Champion",
    },
    
    // Champion Page
    champion: {
      tournamentComplete: "Tournoi Terminé",
      newTournament: "Nouveau Tournoi",
      roster: "Effectif de l'équipe",
      viewBracket: "Voir le Tableau",
      viewLeague: "Voir la Ligue",
    },
    
    // Share Dialog
    shareDialog: {
      title: "Partager le Tournoi",
      description: "Partagez ce lien pour permettre aux autres de voir le tournoi",
      shareLink: "Lien de Partage",
      orShare: "Ou partagez avec le nom et le code:",
      name: "Nom",
      code: "Code",
      copied: "Lien copié!",
    },
    
    // Toasts
    toast: {
      tournamentCreated: "Tournoi créé!",
      tournamentUpdated: "Tournoi mis à jour!",
      scoreRecorded: "Score enregistré!",
      matchReset: "Match réinitialisé!",
      logoAdded: "Logo ajouté!",
      logoRemoved: "Logo supprimé",
      logoFailed: "Échec du téléchargement du logo",
      tournamentDeleted: "Tournoi supprimé",
      tournamentFound: "Tournoi trouvé!",
      tournamentNotFound: "Tournoi non trouvé",
      enterValidScores: "Veuillez entrer des scores valides",
      scoresCannotBeTied: "Les scores ne peuvent pas être égaux - il doit y avoir un gagnant",
      scoresCannotBeNegative: "Les scores ne peuvent pas être négatifs",
      noPermission: "Vous n'avez pas la permission de modifier les scores",
      noSchedulePermission: "Vous n'avez pas la permission de modifier le calendrier",
      logoTooLarge: "Le logo doit faire moins de 500Ko",
      addAtLeast2: "Ajoutez au moins 2 participants",
      bronzeNeeds4: "Le match de bronze nécessite au moins 4 participants",
      viewOnly: "Lecture seule - vous ne pouvez pas modifier les scores",
      viewOnlySchedule: "Lecture seule - vous ne pouvez pas modifier le calendrier",
      viewOnlyReset: "Lecture seule - vous ne pouvez pas réinitialiser les matchs",
      linkCopied: "Lien copié!",
      codeCopied: "Code du tournoi copié!",
      linkCopyFailed: "Échec de la copie du lien",
      enterNameAndCode: "Veuillez entrer le nom et le code du tournoi",
      shuffled: "Participants mélangés!",
      scheduleUpdated: "Calendrier mis à jour!",
      createFailed: "Échec de la création du tournoi",
      nameExists: "Ce nom de tournoi existe déjà. Veuillez utiliser un nom différent.",
      codeExists: "Ce code de tournoi existe déjà. Veuillez utiliser un code différent.",
      bothExist: "Le nom et le code du tournoi existent déjà. Veuillez utiliser des valeurs différentes.",
    },
    
    // Sports
    sports: {
      football: "Football",
      basketball: "Basketball",
      badminton: "Badminton",
      pingpong: "Ping-Pong",
      tennis: "Tennis",
      volleyball: "Volleyball",
      esports: "Esports",
      boardgames: "Jeux de Société",
      custom: "Sport Personnalisé",
    },
  },
  
  es: {
    auth: {
      signIn: "Iniciar sesión",
      signUp: "Registrarse",
      signOut: "Cerrar sesión",
      login: "Entrar",
      register: "Crear cuenta",
      email: "Correo electrónico",
      password: "Contraseña",
      name: "Nombre",
      continueWithGoogle: "Continuar con Google",
      or: "o",
      noAccount: "¿No tienes una cuenta?",
      haveAccount: "¿Ya tienes una cuenta?",
      loginTitle: "Bienvenido de nuevo",
      loginSubtitle: "Inicia sesión para crear y gestionar tus torneos",
      registerTitle: "Crea tu cuenta",
      registerSubtitle: "Regístrate para empezar a crear torneos",
      unverified: "Correo no verificado",
      signInRequired: "Inicia sesión para crear torneos",
      loggingIn: "Iniciando sesión...",
      emailPlaceholder: "tu@ejemplo.com",
      passwordPlaceholder: "Tu contraseña",
      namePlaceholder: "Tu nombre",
      account: "Cuenta",
      profile: "Perfil",
      emailVerification: "Verificación de correo",
      verified: "Verificado",
      notVerified: "No verificado",
      verifyEmailTitle: "Verifica tu correo",
      verifyEmailDesc: "Enviamos un código de 6 dígitos a tu correo. Introdúcelo abajo para verificar. La verificación es opcional: puedes seguir usando todas las funciones igualmente.",
      alreadyVerified: "Tu dirección de correo está verificada.",
      googleVerifiedNote: "Iniciaste sesión con Google: tu correo ya está verificado.",
      sendCode: "Enviar correo de verificación",
      resendCode: "Reenviar correo",
      enterCode: "Introduce el código de 6 dígitos",
      verifyBtn: "Verificar correo",
      verifying: "Verificando...",
      sending: "Enviando...",
      codeSent: "Correo de verificación enviado: revisa tu bandeja de entrada.",
      verifiedToast: "¡Correo verificado!",
      passwordSection: "Acceso con contraseña",
      createPasswordDesc: "Iniciaste sesión con Google. Crea una contraseña para acceder también con tu correo y contraseña.",
      changePasswordDesc: "Actualiza la contraseña que usas para acceder con correo.",
      newPassword: "Nueva contraseña",
      createPassword: "Crear contraseña",
      updatePassword: "Actualizar contraseña",
      savingPassword: "Guardando...",
      passwordSetToast: "Contraseña guardada: ahora puedes acceder con tu correo y contraseña.",
    },
    stats: {
      playerStats: "Estadísticas de jugadores",
      topScorer: "Máximo goleador",
      topAssists: "Máximas asistencias",
      topScorers: "Máximos goleadores",
      topAssisters: "Máximos asistentes",
      topGA: "Top G+A",
      topGADesc: "Goles + asistencias combinados",
      total: "Total",
      allPlayers: "Todos los jugadores",
      player: "Jugador",
      team: "Equipo",
      goals: "Goles",
      assists: "Asistencias",
      goalsShort: "goles",
      goalsAbbr: "G",
      assistsAbbr: "A",
      enterPlayerStats: "Introduce goles y asistencias por jugador",
      goalsMustEqualScore: "El total de goles debe ser igual al marcador del equipo",
      noStatsYet: "Aún no hay estadísticas de jugadores",
    },
    // Common
    appName: "Botoolat",
    tagline: "Crea. Compite. Corona un Campeón.",
    loading: "Cargando...",
    save: "Guardar",
    cancel: "Cancelar",
    close: "Cerrar",
    confirm: "Confirmar",
    delete: "Eliminar",
    edit: "Editar",
    view: "Ver",
    share: "Compartir",
    export: "Exportar",
    back: "Atrás",
    next: "Siguiente",
    
    // Home Page
    home: {
      title: "Donde Comienza Cada Competición",
      subtitle: "Crea torneos profesionales en minutos. Elige un formato, añade participantes, sigue los resultados y genera automáticamente clasificaciones y cuadros.",
      createNew: "Crear Torneo",
      getStarted: "Comenzar",
      createTournamentDesc: "Elige un formato, añade equipos o jugadores, establece las reglas y comienza tu competición.",
      viewTournament: "Ver Torneo",
      viewTournamentDesc: "Sigue las clasificaciones en vivo, los resultados de los partidos, los calendarios y el progreso del cuadro.",
      tournamentName: "Nombre del Torneo",
      enterCode: "Código del Torneo",
      viewBtn: "Ver",
      yourTournaments: "Tus Torneos",
      noTournaments: "No hay torneos aún. ¡Crea tu primero!",
      resume: "Continuar",
      completed: "Completado",
      players: "jugadores",
      teams: "equipos",
      deleteTournament: "¿Eliminar Torneo?",
      deleteConfirm: "Esto eliminará permanentemente este torneo. Esta acción no se puede deshacer.",
    },
    
    // Setup Page
    setup: {
      step1: "Paso 1: Información Básica",
      step2: "Paso 2: Formato",
      step3Title: "Paso 3: Participantes",
      step3TitleLeague: "Paso 3: Equipos",
      tournamentName: "Nombre del Torneo",
      tournamentCode: "Código del Torneo",
      codeHint: "Comparte este código y el nombre del torneo con otros para que puedan ver el torneo",
      enablePlayerStats: "Activar estadísticas de jugadores",
      enablePlayerStatsDesc: "Registra goles y asistencias por jugador y ve a los líderes",
      playerName: "Nombre del jugador",
      addPlayer: "Añadir jugador",
      codeMinLength: "El código del torneo debe tener al menos 4 caracteres",
      selectSport: "Seleccionar Deporte",
      customSport: "Nombre del Deporte Personalizado",
      chooseFormat: "Siguiente: Elegir Formato",
      league: "Liga",
      leagueDesc: "Formato todos contra todos",
      bracket: "Llave",
      bracketDesc: "Torneo de eliminación directa",
      optionalFeatures: "Opciones (Elige Una)",
      doubleElimination: "Doble Eliminación",
      doubleElimDesc: "Pierde dos veces para ser eliminado. Los perdedores tienen una segunda oportunidad.",
      bronzeMedal: "Partido por el Bronce",
      bronzeMedalDesc: "Partido por el 3er lugar. Los perdedores de semifinales compiten.",
      enterParticipant: "Ingresa el nombre del participante",
      enterTeam: "Ingresa el nombre del equipo",
      clickToAddLogo: "Haz clic en el número para agregar un logo",
      addAtLeast: "Agrega al menos 2",
      participants: "participantes",
      shuffle: "Mezclar",
      generateBracket: "Generar Llave",
      generateLeague: "Generar Liga",
      editTournament: "Editar Torneo",
      saveChanges: "Guardar Cambios",
      rebuildTitle: "¿Reconstruir el Torneo?",
      rebuildWarning: "Cambiar los participantes o el formato reconstruirá el torneo. Esto restablecerá todos los resultados de los partidos y no se puede deshacer.",
      rebuildConfirm: "Reconstruir y Restablecer",
      nextAddParticipants: "Siguiente: Agregar Participantes",
      nameExists: "Este nombre de torneo ya existe",
      codeExists: "Este código de torneo ya existe",
      bothExist: "El nombre y el código ya existen",
    },
    
    // Bracket Page
    bracket: {
      winnersBracket: "Llave de Ganadores",
      losersBracket: "Llave de Perdedores",
      grandFinal: "Gran Final",
      grandFinalReset: "Gran Final de Reinicio",
      bronzeMatch: "Partido por el Bronce",
      thirdPlace: "Partido por el 3er Lugar",
      champion: "Campeón",
      finalStandings: "Clasificación Final",
      gamesLeft: "partidos restantes",
      gameLeft: "partido restante",
      viewOnly: "Solo Lectura",
      waitingForSemis: "Esperando Semifinales",
      winnersChampVsLosers: "Campeón de Ganadores vs Campeón de Perdedores",
      rank: "Pos",
      player: "Jugador",
      wins: "Victorias",
      losses: "Derrotas",
      points: "Puntos",
      enterScores: "Ingresar Marcadores",
      vs: "VS",
      bye: "Libre",
      tbd: "Por Definir",
    },
    
    // League Page
    league: {
      standings: "Clasificación",
      matches: "Partidos",
      pendingMatches: "Partidos Pendientes",
      completedMatches: "Partidos Completados",
      round: "Jornada",
      played: "Jugados",
      won: "Ganados",
      drawn: "Empatados",
      lost: "Perdidos",
      goalsFor: "Goles a Favor",
      goalsAgainst: "Goles en Contra",
      goalDiff: "Dif. Goles",
      viewChampion: "Ver Campeón",
    },
    
    // Champion Page
    champion: {
      tournamentComplete: "Torneo Completado",
      newTournament: "Nuevo Torneo",
      roster: "Plantilla del equipo",
      viewBracket: "Ver Llave",
      viewLeague: "Ver Liga",
    },
    
    // Share Dialog
    shareDialog: {
      title: "Compartir Torneo",
      description: "Comparte este enlace para que otros puedan ver el torneo",
      shareLink: "Enlace para Compartir",
      orShare: "O comparte usando nombre y código:",
      name: "Nombre",
      code: "Código",
      copied: "¡Enlace copiado!",
    },
    
    // Toasts
    toast: {
      tournamentCreated: "¡Torneo creado!",
      tournamentUpdated: "¡Torneo actualizado!",
      scoreRecorded: "¡Marcador registrado!",
      matchReset: "¡Partido reiniciado!",
      logoAdded: "¡Logo agregado!",
      logoRemoved: "Logo eliminado",
      logoFailed: "Error al cargar el logo",
      tournamentDeleted: "Torneo eliminado",
      tournamentFound: "¡Torneo encontrado!",
      tournamentNotFound: "Torneo no encontrado",
      enterValidScores: "Por favor ingresa marcadores válidos",
      scoresCannotBeTied: "Los marcadores no pueden estar empatados - debe haber un ganador",
      scoresCannotBeNegative: "Los marcadores no pueden ser negativos",
      noPermission: "No tienes permiso para editar los marcadores",
      noSchedulePermission: "No tienes permiso para editar el calendario",
      logoTooLarge: "El logo debe ser menor a 500KB",
      addAtLeast2: "Agrega al menos 2 participantes",
      bronzeNeeds4: "El partido por el bronce requiere al menos 4 participantes",
      viewOnly: "Solo lectura - no puedes editar los marcadores",
      viewOnlySchedule: "Solo lectura - no puedes editar el calendario",
      viewOnlyReset: "Solo lectura - no puedes reiniciar los partidos",
      linkCopied: "¡Enlace copiado!",
      codeCopied: "¡Código del torneo copiado!",
      linkCopyFailed: "Error al copiar el enlace",
      enterNameAndCode: "Por favor ingresa el nombre y código del torneo",
      shuffled: "¡Participantes mezclados!",
      scheduleUpdated: "¡Calendario actualizado!",
      createFailed: "Error al crear el torneo",
      nameExists: "Este nombre de torneo ya existe. Por favor usa un nombre diferente.",
      codeExists: "Este código de torneo ya existe. Por favor usa un código diferente.",
      bothExist: "El nombre y código del torneo ya existen. Por favor usa valores diferentes.",
    },
    
    // Sports
    sports: {
      football: "Fútbol",
      basketball: "Baloncesto",
      badminton: "Bádminton",
      pingpong: "Ping Pong",
      tennis: "Tenis",
      volleyball: "Voleibol",
      esports: "Esports",
      boardgames: "Juegos de Mesa",
      custom: "Deporte Personalizado",
    },
  },
};

export const languageNames = {
  en: "English",
  ar: "العربية",
  fr: "Français",
  es: "Español",
};

export const languageFlags = {
  en: "🇺🇸",
  ar: "🇰🇼",
  fr: "🇫🇷",
  es: "🇪🇸",
};
